import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Box, Paper, Tab, Tabs, Typography, withStyles } from "@material-ui/core";
import * as actions from "./store/billingPlanActions";
import LoadingDialog from "../BillingAccount/Details/LoadingDialog";
import { BackButton, hasPermission } from "../../sc-utilities";
import { parseURL, updateURL } from "../../sc-utilities";
import PlanForm from "./PlanForm";
import * as Yup from "yup";
import PlanSaveModal from "./PlanSaveModal";
import History from '../../../History';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        margin: theme.spacing(),
        [theme.breakpoints.down('md')]: {
            width: `97.8%`
        },
        [theme.breakpoints.down('sm')]: {
            width: `97.8%`
        }
    },
});

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index || props.disabled}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3} style={{ padding: '0px' }} disabled={true}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const TabContainer = props => {
    return (
        <Typography component="div" dir={props.dir} style={{ padding: 3 * 3 }}>
            {props.children}
        </Typography>
    );
};

const getTabValue = () => {
    let urlArray = window.location.href.split('#');
    return urlArray.length > 1 ? Number(urlArray[1]) : 0;
};

const PlanEdit = (props) => {
    const { permissions, onLoadInit, billingPlan, classes, theme, activeCustomerID, translations, onFetchBillingPlan, onEditBillingPlan } = props;
    const [tabValue, setTabValue] = useState(getTabValue());
    const { Configuration, PriceList } = billingPlan ? billingPlan : {};

    const configuration = typeof Configuration === "string" ? JSON.parse(Configuration) : null;
    const planPriceList = billingPlan && billingPlan.PriceList && typeof billingPlan.PriceList === "string" ? JSON.parse(billingPlan.PriceList) : {}

    const [modalState, setModalState] = useState({ open: false, values: {} });

    useEffect(() => {
        const fetchData = async () => {
            await onFetchBillingPlan({ VersionID: parseURL(window.location.href).VersionID, ActiveCustomerID: activeCustomerID });
        }
        fetchData();

        return () => {
            onLoadInit()
        }
    }, [activeCustomerID, onFetchBillingPlan, onLoadInit])

    const handleChange = (_event, value) => {
        updateURL(value);
        setTabValue(value);
    };

    const submitHandler = async data => {
        let requestData = { ...data };

        const config = !(billingPlan.PostpaidAllowed && !billingPlan.PrepaidAllowed) ?
            {
                MaxAllowedUsers: requestData.MaxAllowedUsers,
                MaxRemoteDiskNumber: requestData.MaxRemoteDiskNumber,
                ErasureRedemptionPeriod: requestData.ErasureRedemptionPeriod,
                DiagnoseRedemptionPeriod: requestData.DiagnoseRedemptionPeriod,
                CheckRedemptionPeriod: requestData.CheckRedemptionPeriod,
                VerificationRedemptionPeriod: requestData.VerificationRedemptionPeriod,
                AppleFullCheckPrice: requestData.AppleFullCheckPrice,
                AppleSimlockCheckPrice: requestData.AppleSimlockCheckPrice,
                GsmaCheckPrice: requestData.GsmaCheckPrice,
                SamsungWorldwideCheckPrice: requestData.SamsungWorldwideCheckPrice,
                MdmWorkCheckPrice: requestData.MdmWorkCheckPrice,
                AppleICloudLockCheckPrice: requestData.AppleICloudLockCheckPrice,
                AutopilotCheckPrice: requestData.AutopilotCheckPrice,
            } : requestData;

        const priceList = requestData?.['Mobile Erasure'] ? JSON.stringify({
            mobile: requestData['Mobile Erasure'],
            mobile_erasure_lite: requestData['Mobile Erasure Lite'],
            mobile_verifier: requestData['Mobile Erasure Verification'],
            mobile_diagnose: requestData['Mobile Diagnose'],
            mobile_check: requestData['Mobile Check'],
            work: requestData['Work Erasure'],
            work_verifier: requestData['Work Erasure Verification'],
            work_diagnose: requestData['Work Diagnose'],
            work_check: requestData['Work Check'],
            watch: requestData['Watch Erasure'],
            watch_diagnose: requestData['Watch Diagnose'],
            watch_check: requestData['Watch Check'],
            headphone_diagnose: requestData['Headphone Diagnose'],
            headphone_check: requestData['Headphone Check'],
            remote: requestData.Remote
        }) : PriceList

        const editVersion = requestData.VersionType === "editVersion";

        const result = await onEditBillingPlan({ ...requestData, EditCurrentVersion: editVersion, PlanID: billingPlan?.PlanID, VersionID: billingPlan?.VersionID, Configuration: requestData.MaxAllowedUsers ? JSON.stringify(config) : Configuration, PriceList: priceList, ActiveCustomerID: activeCustomerID });
        if (result) {
            History.replace(`/billing/plans/edit?VersionID=${result?.VersionID}#${tabValue}`);
            setModalState({ open: false, values: {} });
        }
    }

    const propsToPass = {
        submitHandler,
        translations,
        permissions,
        isEdit: true,
        billingPlan,
        modalState,
        setModalState
    }

    const details = {
        initialValues: {
            Name: billingPlan && billingPlan.Name ? billingPlan.Name : '',
            Description: billingPlan && billingPlan.Description ? billingPlan.Description : '',
            Status: billingPlan && billingPlan.Status ? billingPlan.Status : 'default',
            ValidFrom: billingPlan && billingPlan.ValidFrom ? billingPlan.ValidFrom : null,
            ValidTo: billingPlan && billingPlan.ValidTo ? billingPlan.ValidTo : null,
            PrepaidAllowed: billingPlan ? !!billingPlan.PrepaidAllowed : false,
            PostpaidAllowed: billingPlan ? !!billingPlan.PostpaidAllowed : false
        },
        formSettings: {
            Validation: {
                Name: Yup.string().required('Required'),
                Description: Yup.string().required('Required'),
                Status: Yup.string().required('Required'),
                ValidFrom: Yup.date()
                    .notRequired()
                    .nullable(true),
                ValidTo: Yup.date()
                    .notRequired()
                    .nullable(true).min(Yup.ref('ValidFrom')),
            },
            ButtonSubmit: hasPermission(permissions, 'plan_update') ? {
                Title: translations['save'] || 'SAVE',
                Color: 'primary'
            } : null,
        }
    };

    const config = {
        initialValues: {
            MinimalUsage: configuration?.MinimalUsage ?? 100,
            IncludedUsage: configuration?.IncludedUsage ?? 100,
            AllowedOveruse: configuration?.AllowedOveruse ?? '',
            MaxAllowedUsers: configuration?.MaxAllowedUsers ?? 5,
            MaxRemoteDiskNumber: configuration?.MaxRemoteDiskNumber ?? 5,
            MonthlyPrice: configuration?.MonthlyPrice ?? '0',
            ErasureRedemptionPeriod: configuration?.ErasureRedemptionPeriod ?? 30,
            DiagnoseRedemptionPeriod: configuration?.DiagnoseRedemptionPeriod ?? 30,
            CheckRedemptionPeriod: configuration?.CheckRedemptionPeriod ?? 30,
            VerificationRedemptionPeriod: configuration?.VerificationRedemptionPeriod ?? 30,
            AppleFullCheckPrice: configuration?.AppleFullCheckPrice ?? 0.0,
            AppleFmipCheckPrice: configuration?.AppleFmipCheckPrice ?? 0.0,
            AppleSimlockCheckPrice: configuration?.AppleSimlockCheckPrice ?? 0.0,
            ManufacturerData: configuration?.ManufacturerData ?? 0.0,
            GsmaCheckPrice: configuration?.GsmaCheckPrice ?? 0.0,
            UsCarrierCheck: configuration?.UsCarrierCheck ?? 0.0,
            SamsungWorldwideCheckPrice: configuration?.SamsungWorldwideCheckPrice ?? 0.0,
            MdmWorkCheckPrice: configuration?.MdmWorkCheckPrice ?? 0.0,
            AppleICloudLockCheckPrice: configuration?.AppleICloudLockCheckPrice ?? 0.0,
            AutopilotCheckPrice: configuration?.AutopilotCheckPrice ?? 0.0,
        },
        formSettings: {
            Validation: {
                AllowedOveruse: Yup.number().min(0).notRequired(),
                MinimalUsage: Yup.number().required('Required').required('Required').min(0),
                IncludedUsage: Yup.number().required('Required').required('Required').min(0),
                MaxAllowedUsers: Yup.number().required('Required').required('Required').min(0),
                MaxRemoteDiskNumber: Yup.number().required('Required').required('Required').min(0),
                MonthlyPrice: Yup.number().required('Required').required('Required').min(0),
                ErasureRedemptionPeriod: Yup.number().required(),
                DiagnoseRedemptionPeriod: Yup.number().required(),
                CheckRedemptionPeriod: Yup.number().required(),
                VerificationRedemptionPeriod: Yup.number().required(),
                AppleFullCheckPrice: Yup.number().notRequired().min(0.0),
                AppleSimlockCheckPrice: Yup.number().notRequired().min(0.0),
                GsmaCheckPrice: Yup.number().notRequired().min(0.0),
                SamsungWorldwideCheckPrice: Yup.number().notRequired().min(0.0),
                MdmWorkCheckPrice: Yup.number().notRequired().min(0.0),
                AppleICloudLockCheckPrice: Yup.number().notRequired().min(0.0),
                AutopilotCheckPrice: Yup.number().notRequired().min(0.0),
            },
            ButtonSubmit: hasPermission(permissions, 'plan_update') ? {
                Title: translations['save'] || 'SAVE',
                Color: 'primary'
            } : null,
        }
    };

    const priceListValidation = Yup.array().of(
        Yup.object().shape({
            from: Yup.number().integer().required(),
            to: Yup.number().integer().nullable(),
            price: Yup.number().min(0.1).required()
        })
    ).test('Test', translations['from_must_be_greater_than_previous'] || 'From must be greater than previous from! ', (values) => {
        return !values.some((value, index) => values[index - 1] ? (value.from <= values[index - 1].from && (value.to === null || value.from <= value.to)) : false);
    });

    const priceList = {
        initialValues: {
            'Mobile Erasure': planPriceList && planPriceList.mobile ? planPriceList.mobile : [],
            'Mobile Erasure Lite': planPriceList && planPriceList.mobile_erasure_lite ? planPriceList.mobile_erasure_lite : [],
            'Mobile Erasure Verification': planPriceList && planPriceList.mobile_verifier ? planPriceList.mobile_verifier : [],
            'Mobile Diagnose': planPriceList && planPriceList.mobile_diagnose ? planPriceList.mobile_diagnose : [],
            'Mobile Check': planPriceList && planPriceList.mobile_check ? planPriceList.mobile_check : [],
            'Work Erasure': planPriceList && planPriceList.work ? planPriceList.work : [],
            'Work Erasure Verification': planPriceList && planPriceList.work_verifier ? planPriceList.work_verifier : [],
            'Work Diagnose': planPriceList && planPriceList.work_diagnose ? planPriceList.work_diagnose : [],
            'Work Check': planPriceList && planPriceList.work_check ? planPriceList.work_check : [],
            'Watch Erasure': planPriceList && planPriceList.watch ? planPriceList.watch : [],
            'Watch Diagnose': planPriceList && planPriceList.watch_diagnose ? planPriceList.watch_diagnose : [],
            'Watch Check': planPriceList && planPriceList.watch_check ? planPriceList.watch_check : [],
            'Headphone Diagnose': planPriceList && planPriceList.headphone_diagnose ? planPriceList.headphone_diagnose : [],
            'Headphone Check': planPriceList && planPriceList.headphone_check ? planPriceList.headphone_check : [],
            Remote: planPriceList && planPriceList.remote ? planPriceList.remote : []
        },
        formSettings: {
            Validation: {
                'Mobile Erasure': priceListValidation,
                'Mobile Erasure Lite': priceListValidation,
                'Mobile Erasure Verification': priceListValidation,
                'Mobile Diagnose': priceListValidation,
                'Mobile Check': priceListValidation,
                'Work Erasure': priceListValidation,
                'Work Erasure Verification': priceListValidation,
                'Work Diagnose': priceListValidation,
                'Work Check': priceListValidation,
                'Watch Erasure': priceListValidation,
                'Watch Diagnose': priceListValidation,
                'Watch Check': priceListValidation,
                'Headphone Diagnose': priceListValidation,
                'Headphone Check': priceListValidation,
                Remote: priceListValidation
            },
            ButtonSubmit: hasPermission(permissions, 'plan_update') ? {
                Title: translations['save'] || 'SAVE',
                Color: 'primary'
            } : null,
        }
    };

    return (
        !billingPlan ? <LoadingDialog /> :
            <>
                <Paper className={classes.root}>
                    {BackButton(translations)}
                    <Tabs
                        value={tabValue}
                        onChange={handleChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab label={translations['edit_plan'] || "Edit plan"} />
                        <Tab label={translations['configuration'] || "Configuration"} />
                        <Tab label={translations['price_list'] || "Price list"} />
                    </Tabs>
                </Paper>
                <Paper className={classes.root}>

                    <TabPanel index={0} value={tabValue}>
                        <TabContainer dir={theme.direction}>
                            <PlanForm activeStep={0} {...propsToPass} settingsAndValues={details} />
                        </TabContainer>
                    </TabPanel>
                    <TabPanel index={1} value={tabValue}>
                        <TabContainer dir={theme.direction}>
                            <PlanForm activeStep={1} {...propsToPass} settingsAndValues={config} />
                        </TabContainer>
                    </TabPanel>
                    <TabPanel index={2} value={tabValue}>
                        <TabContainer dir={theme.direction}>
                            <PlanForm activeStep={2} {...propsToPass} settingsAndValues={priceList} />
                        </TabContainer>
                    </TabPanel>
                </Paper>
                <PlanSaveModal {...propsToPass} />
            </>
    )
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        billingPlan: state?.billing?.plans?.loaded
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLoadInit: () => dispatch(actions.loadBillingPlanInit()),
        onFetchBillingPlan: billingPlan => dispatch(actions.loadBillingPlan(billingPlan)),
        onEditBillingPlan: billingPlan => dispatch(actions.editBillingPlan(billingPlan)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(PlanEdit));